// Security Mixin - PCI Compliance helpers for Vue components
import apiProxy from '@/services/apiProxy';
import securityService from '@/services/securityService';
import sessionService from '@/services/sessionService';
import { brandConfig } from '@/config/brandConfig';

export const securityMixin = {
  data() {
    return {
      isSecureSession: false,
      currentBrand: null
    };
  },

  async created() {
    await this.initializeSecurity();
  },

  methods: {
    // Initialize security for component
    async initializeSecurity() {
      try {
        if (sessionService.isAuthenticated()) {
          // Don't validate token with auth API - central proxy handles validation
          this.isSecureSession = true;
          // Extract brand from token if available
          const token = sessionService.getToken();
          if (token && token.includes('.')) {
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              this.currentBrand = payload.brandCode;
            } catch {
              this.currentBrand = null;
            }
          }
        }
      } catch (error) {
        console.error('Security initialization failed:', error);
        this.handleSecurityError(error);
      }
    },

    // Secure API call wrapper
    async secureApiCall(apiName, data = {}, options = {}) {
      try {
        // Central proxy handles all token validation
        return await apiProxy.call(apiName, data, options);
      } catch (error) {
        this.handleSecurityError(error);
        throw error;
      }
    },

    // Brand-aware API call
    async brandApiCall(apiName, data = {}, options = {}) {
      if (!this.currentBrand) {
        throw new Error('Brand context required');
      }
      
      return await apiProxy.callWithBrand(apiName, this.currentBrand, data, options);
    },

    // Get brand configuration
    getBrandConfig() {
      return this.currentBrand ? brandConfig.getBrand(this.currentBrand) : null;
    },

    // Handle security errors
    handleSecurityError(error) {
      if (error.message.includes('Authentication failed') || 
          error.message.includes('Session expired')) {
        this.redirectToLogin();
      } else if (error.message.includes('Too many validation attempts')) {
        this.showSecurityMessage('Please wait 5 seconds before trying again.');
      }
    },

    // Redirect to login
    redirectToLogin() {
      sessionService.clearToken();
      this.$router.push('/login');
    },

    // Show security message
    showSecurityMessage(message) {
      // Override in components for custom message display
      console.warn('Security message:', message);
    },

    // Secure logout
    async secureLogout() {
      try {
        await apiProxy.call('logout');
      } catch (error) {
        console.error('Logout API call failed:', error);
      } finally {
        sessionService.clearToken();
        this.$router.push('/login');
      }
    },

    // Remove UUID references (PCI compliance)
    sanitizeData(data) {
      const sanitized = { ...data };
      delete sanitized.uuid;
      delete sanitized.user_uuid;
      delete sanitized.customer_uuid;
      return sanitized;
    }
  }
};